import { useEffect } from "react";
import { ClearIcon } from "./Icons";
import { palette } from "./tokens";

/**
 * A bottom sheet, constrained to its parent rather than the window.
 *
 * It renders `absolute inset-0` inside the screen container, so it stays within
 * the phone's boundaries instead of escaping to the viewport — which also means
 * it behaves correctly inside the preview frame rather than covering the whole
 * desktop page.
 *
 * Rising from the bottom rather than centring: on a phone the bottom third is
 * the only comfortably reachable part of the screen, and settings are something
 * you adjust one-handed mid-game.
 *
 * The parent must be positioned.
 */
/**
 * Which edge the panel is anchored to. A sheet should arrive from the control
 * that opened it: the spice cog sits in the setup header, so its panel comes
 * down from the top rather than up from a corner nothing was tapped in.
 */
export enum SheetFrom {
  Bottom = "bottom",
  Top = "top",
}

export const Sheet = ({
  open,
  onClose,
  title,
  from = SheetFrom.Bottom,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  from?: SheetFrom;
  children: React.ReactNode;
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const atTop = from === SheetFrom.Top;

  return (
    <div
      className={`absolute inset-0 z-30 flex flex-col ${atTop ? "justify-start" : "justify-end"}`}
    >
      {/* A button rather than a div, so dismissing works by keyboard too. */}
      <button
        aria-label="Close settings"
        onClick={onClose}
        className="absolute inset-0"
        style={{ backgroundColor: `${palette.bg}C4` }}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`relative z-10 pb-6 pt-4 ${atTop ? "rounded-b-2xl" : "rounded-t-2xl"}`}
        style={{
          // `bg`, not `surface`: the spice control's track is `surface`, and a
          // surface-on-surface panel makes it vanish.
          backgroundColor: palette.bg,
          boxShadow: atTop
            ? `inset 0 -1px 0 ${palette.inkDim}33`
            : `inset 0 1px 0 ${palette.inkDim}33`,
        }}
      >
        {/* Grab handle: signals the sheet is dismissible without a label. It
            follows the anchored edge, so it always sits on the outer side. */}
        {!atTop && (
          <div
            className="mx-auto mb-4 h-1 w-10 rounded-full"
            style={{ backgroundColor: palette.inkDim, opacity: 0.4 }}
          />
        )}

        <div className="flex items-start justify-between gap-4 px-5 pb-4">
          <h2 className="text-base font-bold uppercase leading-tight tracking-tight">
            {title}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
          >
            <ClearIcon color={palette.inkDim} size={18} />
          </button>
        </div>

        <div className="px-5">{children}</div>

        {atTop && (
          <div
            className="mx-auto mt-4 h-1 w-10 rounded-full"
            style={{ backgroundColor: palette.inkDim, opacity: 0.4 }}
          />
        )}
      </div>
    </div>
  );
};
